import ModalDialog, {
	ModalBody,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Box } from '@atlaskit/primitives';
import { useState } from 'react';

import { useAnalyticsWebClient } from '../../../../utils/analytics/context';
import { Form } from '../../shared/components/create-flow-form';
import type { SessionIssue } from '../../utils/issue-json';

type RepositoryModalProps = {
	isSubmitPending?: boolean;
	selectedIssue?: SessionIssue;
	onClose: () => void;
	onSubmit: (repositoryUrl: string, branchName: string, prompt: string) => void;
	initialBranchName: string;
};

type RepositoryModalWrapperProps = {
	isOpen: boolean;
} & RepositoryModalProps;

const RepositoryModalWrapper = ({
	isOpen,
	isSubmitPending,
	onClose,
	onSubmit,
	initialBranchName,
	selectedIssue,
}: RepositoryModalWrapperProps) => {
	return (
		<ModalTransition>
			{isOpen && (
				<RepositoryModalDialog
					initialBranchName={initialBranchName}
					isSubmitPending={isSubmitPending}
					onClose={onClose}
					onSubmit={onSubmit}
					selectedIssue={selectedIssue}
				/>
			)}
		</ModalTransition>
	);
};

const RepositoryModalDialog = ({
	isSubmitPending,
	onClose,
	onSubmit,
	initialBranchName,
	selectedIssue,
}: RepositoryModalProps) => {
	return (
		<ModalDialog onClose={onClose}>
			<ModalHeader hasCloseButton>
				{/* eslint-disable-next-line @dev-agents/rules/i18n/text-wrapped-in-format-message */}
				<ModalTitle>Create a new flow</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<Box paddingBlockEnd="space.300">
					<Form
						initialBranchName={initialBranchName}
						isCreatingFlow={isSubmitPending}
						onCancel={onClose}
						onSubmit={onSubmit}
						selectedIssue={selectedIssue}
					/>
				</Box>
			</ModalBody>
		</ModalDialog>
	);
};

export const useRepositoryModal = ({
	branchName,
	onConfirm,
	isSubmitPending,
	selectedIssue,
}: {
	branchName: string;
	onConfirm: (repositoryUrl: string, branchName: string, prompt: string) => void;
	isSubmitPending?: boolean;
	selectedIssue?: SessionIssue;
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const analyticsWebClient = useAnalyticsWebClient();

	const openModal = () => {
		setIsOpen(true);

		analyticsWebClient.sendUIEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'start-session',
			source: 'boysenberry-home',
		});
	};

	const closeModal = () => {
		setIsOpen(false);
	};

	const handleConfirm = (repositoryUrl: string, branchName: string, prompt: string) => {
		onConfirm(repositoryUrl, branchName, prompt);

		analyticsWebClient.sendUIEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'confirm-repository',
			source: 'boysenberry-home',
		});
	};

	return {
		openModal,
		closeModal,
		RepositoryModal: (
			<RepositoryModalWrapper
				initialBranchName={branchName}
				selectedIssue={selectedIssue}
				isOpen={isOpen}
				onClose={closeModal}
				onSubmit={handleConfirm}
				isSubmitPending={isSubmitPending}
			/>
		),
	};
};
